import { snake2Camel, camel2Snake } from './text';

// Filters
export const only = only => prop => only.includes(prop);
export const onlyProps = only => (_, prop) => only.includes(prop);
export const except = except => prop => !except.includes(prop);
export const exceptProps = except => (_, prop) => !except.includes(prop);
export const nonNulls = val => val !== null;

export const flatWhen = condition => parent => Object
  .entries(parent)
  .reduce((carrier, [prop, value]) => Object
    .assign(
      carrier,
      condition.call(undefined, prop, parent) && isObject(value) ? value : { [prop]: value }
    ), {}
  );

export const isObject = x =>
  typeof x === 'object'
    && x !== null
    && Object.getPrototypeOf(x) === Object.prototype;

export const flatOnly = (...props) => flatWhen(only(props));
export const flatExcept = (...props) => flatWhen(except(props));
export const flatAll = flatWhen(isObject);

const _flatIDs = (keepSubObjects = false) => obj => Object
  .entries(obj)
  .reduce((a, [prop, value]) => {
    const hasId = isObject(value) && typeof value.id !== 'undefined';
    const [propName, propValue] = hasId ? [`${prop}Id`, value.id] : [prop, value];
    const subObject = keepSubObjects ? { [prop]: value } : {};

    return Object.assign(a, { [propName]: propValue, ...subObject });
  }, {});

export const flatIDs = _flatIDs(false);
export const flatInnerIds = _flatIDs(true);

export const camelize = obj => Object
  .entries(obj)
  .reduce((a, [prop, value]) => Object.assign(a, { [snake2Camel(prop)]: value }), {});

export const snakeize = obj => Object
  .entries(obj)
  .reduce((a, [prop, value]) => Object.assign(a, { [camel2Snake(prop)]: value }), {});

/**
 * Like Array.some.
 * Execute .some() on object keys and goes deeper if any key value is an object
 */
export const deepSome = obj => Object
  .entries(obj)
  .some(([, value]) => isObject(value)
    ? deepSome(value)
    : !!value
  );

/**
 * Map from object keys
 *
 * @param {Object.<string, Function>} mapping
 * @returns {(obj: {}) => {}}
 */
export const map = mapping => obj => Object
  .entries(obj)
  .reduce((o, [prop, value]) => Object.assign(o, {
    [prop]: prop in mapping ? mapping[prop].call(undefined, value, prop, obj) : value
  }), {});

/**
 * Map all props with a single function
 *
 * @param {Function} mapping
 * @returns {(obj: {}) => {}}
 */
export const mapAll = f => obj => Object
  .entries(obj)
  .reduce((o, [prop, value]) => Object.assign(o, {
    [prop]: f.call(undefined, value, prop, obj)
  }), {});

/**
 * Map from mapping object keys
 *
 * @param {Object.<string, Function>} mapping
 * @returns {(obj: {}) => {}}
 */
export const remap = mapping => obj => Object
  .entries(mapping)
  .reduce((o, [prop, fn]) => Object.assign(o, {
    [prop]: prop in mapping ? fn.call(undefined, obj[prop], prop, obj) : obj[prop]
  }), {});

/**
 * Compose/merge objects in a left-to-right manner.
 *
 * @param {...{}} objs - Objects to merge
 * @returns {{}} The merged object
 */
export const compose = (...objs) => objs.reduce((c, o) => Object.assign(c, o), {});

/**
 * Prebind an object to a `compose` function.
 *
 * @param {{}} obj - The object to be prebound
 * @returns {{}} The merged object
 */
export const join = obj => compose.bind(undefined, obj);

/**
 * HOF. Kind of Array.prototype.filter for Objects.
 *
 * @param {Function} fun - The function that will filter the subject
 * @returns {(obj: {}) => {}}
 */
export const filter = fun => obj => Object
  .entries(obj)
  .reduce((a, [prop, val]) => fun(val, prop, obj) ? Object.assign(a, { [prop]: val }) : a, {});

// Filter only non-null objects
export const onlyNonNulls = filter(nonNulls);

/**
 * Merge objects in backwards fashion to mantain precedence, left to right.
 * The whole idea is to merge objects with `null` props assigning a default when these cases met,
 * without overwriting existing properties.
 * Merge in backwards, so leftiest objects take precedence
 *
 * @param {...{}} objs - The objects to merge
 * @returns {{}} The merged object
 */
export const merge = (...objs) => objs.reduceRight((a, x) => Object.assign(a, onlyNonNulls(x)));
